import { FC, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Grid,
	InputAdornment,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import { TPgFeeMatrix, TPgPaymentMethodType } from 'services/api/paymentGateway/types';
import { PgPanel } from '../common/PgPanel';
import { usePgCall } from '../common/usePgCall';
import { PG_METHOD_TYPE_LABELS } from '../constants';
import { countDecimals, formatMoney, formatPercent, grossUpFee, parseDecimal } from '../utils';
import { effectiveDraftRule, TFeeDrafts } from './draft';

type TFeeCalculatorProps = {
	matrix: TPgFeeMatrix;
	drafts: TFeeDrafts;
	hasChanges: boolean;
};

const DEFAULT_SERVICE = 'default';

/**
 * Preview of the fee for an amount: live estimate with the values on screen (unsaved included) and
 * the exact quote of the gateway with the saved rules (`POST /api/admin/fees/preview`).
 */
export const FeeCalculator: FC<TFeeCalculatorProps> = ({ matrix, drafts, hasChanges }) => {
	const [amount, setAmount] = useState('100');
	const [serviceValue, setServiceValue] = useState<string>(DEFAULT_SERVICE);
	const [methodType, setMethodType] = useState<TPgPaymentMethodType>('card');

	const previewCall = usePgCall(api.paymentGateway.previewFee, {
		fallbackError: 'Failed to calculate the fee',
	});

	const amountEur = parseDecimal(amount);
	const amountValid = amountEur !== null && amountEur > 0 && countDecimals(amount) <= 2;
	const serviceId = serviceValue === DEFAULT_SERVICE ? null : Number(serviceValue);

	const estimate = useMemo(() => {
		if (!amountValid || amountEur === null) return null;
		const rule = effectiveDraftRule(drafts, serviceId, methodType);
		if (!rule) return { rule: null, fee: 0, total: amountEur };
		return { rule, ...grossUpFee(amountEur, rule.percent, rule.fixedAmount) };
	}, [amountValid, amountEur, drafts, serviceId, methodType]);

	const handlePreview = () => {
		if (!amountValid || amountEur === null) return;
		void previewCall.run({
			legalEntityId: matrix.legalEntityId,
			serviceId,
			methodType,
			amountEur,
		});
	};

	const quote = previewCall.result;

	return (
		<PgPanel title="Fee calculator">
			<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
				<TextField
					label="Invoice amount"
					size="small"
					value={amount}
					onChange={(event) => setAmount(event.target.value)}
					error={!amountValid}
					helperText={amountValid ? ' ' : 'A positive amount with up to 2 decimals'}
					inputProps={{ inputMode: 'decimal' }}
					InputProps={{ endAdornment: <InputAdornment position="end">EUR</InputAdornment> }}
					sx={{ width: 180 }}
				/>
				<TextField
					select
					label="Service"
					size="small"
					value={serviceValue}
					onChange={(event) => setServiceValue(event.target.value)}
					helperText=" "
					sx={{ minWidth: 220 }}
				>
					<MenuItem value={DEFAULT_SERVICE}>Default rule only</MenuItem>
					{matrix.services.map((service) => (
						<MenuItem key={service.serviceId} value={String(service.serviceId)}>
							{service.serviceName} ({service.serviceCode})
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					label="Method"
					size="small"
					value={methodType}
					onChange={(event) => setMethodType(event.target.value as TPgPaymentMethodType)}
					helperText=" "
					sx={{ minWidth: 160 }}
				>
					{matrix.methodTypes.map((type) => (
						<MenuItem key={type} value={type}>
							{PG_METHOD_TYPE_LABELS[type] ?? type}
						</MenuItem>
					))}
				</TextField>
				<Button
					variant="outlined"
					onClick={handlePreview}
					disabled={!amountValid || previewCall.loading}
					startIcon={previewCall.loading ? <CircularProgress size={16} /> : undefined}
				>
					Quote with saved rules
				</Button>
			</Box>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Typography variant="subtitle2" color="primary">
						Estimate with the values on screen{hasChanges ? ' (unsaved)' : ''}
					</Typography>
					{estimate ? (
						<Typography variant="body2">
							{estimate.rule
								? `${estimate.rule.source === 'service' ? 'Service rule' : 'Default rule'}: ${formatPercent(estimate.rule.percent)} + ${formatMoney(estimate.rule.fixedAmount, 'EUR')}`
								: 'No active rule — no fee'}
							<br />
							Fee <b>{formatMoney(estimate.fee, 'EUR')}</b>, total charged{' '}
							<b>{formatMoney(estimate.total, 'EUR')}</b>
						</Typography>
					) : (
						<Typography variant="body2" color="text.secondary">
							Enter an amount.
						</Typography>
					)}
				</Grid>
				<Grid item xs={12} md={6}>
					<Typography variant="subtitle2" color="primary">
						Gateway quote (saved rules)
					</Typography>
					{quote ? (
						<Typography variant="body2">
							{quote.feeRuleId
								? `Rule #${quote.feeRuleId}: ${formatPercent(quote.percent)} + ${formatMoney(quote.fixedAmount, 'EUR')}`
								: 'No active rule — no fee'}
							<br />
							Base {formatMoney(quote.baseAmountEur, 'EUR')}, fee{' '}
							<b>{formatMoney(quote.feeEur, 'EUR')}</b>, total{' '}
							<b>{formatMoney(quote.totalEur, 'EUR')}</b>
						</Typography>
					) : (
						<Typography variant="body2" color="text.secondary">
							Press “Quote with saved rules”.
						</Typography>
					)}
				</Grid>
			</Grid>
			<Alert severity="info" sx={{ mt: 2 }}>
				Gross-up: fee = amount / (1 − percent) − amount, rounded up to the cent, plus the fixed
				part. The customer sees and is charged exactly this total.
			</Alert>
		</PgPanel>
	);
};
